/**
 * Public flow portfolio API wrappers — token-based mirror of
 * lib/api/flow-instances/portfolios.ts, used by the client/public flow.
 */

import type {
  AddSwitchPairRequest,
  ExistingSnapshotResponse,
  FlowInstancePortfoliosResponse,
  GoalPortfolioResponse,
  ModelPortfolioCandidatesResponse,
  SwitchPairClearResponse,
  SwitchPairDeleteResponse,
  SwitchPairResponse,
  UpdateExistingHoldingRequest,
  UpdateExistingHoldingResponse,
  UpdateGoalPortfolioRequest,
  UpdateSwitchPairRequest,
} from "@repo/shared/types";
import { delWithToken, getWithToken, patchWithToken, postWithToken } from "../_client";

// --- Portfolios ---

export function getPublicPortfolios(goalId: string, token: string) {
  return getWithToken<GoalPortfolioResponse[]>(`/public/flow/goals/${goalId}/portfolios`, token);
}

/** QL-676 — every portfolio of the session's instance, grouped by goal, in one request. */
export function getPublicInstancePortfolios(token: string) {
  return getWithToken<FlowInstancePortfoliosResponse>("/public/flow/portfolios", token);
}

export function getPublicPortfolio(portfolioId: string, token: string) {
  return getWithToken<GoalPortfolioResponse>(`/public/flow/portfolios/${portfolioId}`, token);
}

export function createPublicPortfolio(
  goalId: string,
  data: Record<string, unknown>,
  token: string,
) {
  return postWithToken<any>(`/public/flow/goals/${goalId}/portfolios`, token, data);
}

export function updatePublicPortfolio(
  portfolioId: string,
  data: UpdateGoalPortfolioRequest,
  token: string,
) {
  return patchWithToken<any>(
    `/public/flow/portfolios/${portfolioId}`,
    token,
    data satisfies UpdateGoalPortfolioRequest,
  );
}

/** QL-72 — model portfolios the client may pick from (all risk scores). */
export function getPublicModelPortfolioCandidates(
  portfolioId: string,
  token: string,
  lang?: string,
) {
  const query = lang ? `?lang=${encodeURIComponent(lang)}` : "";
  return getWithToken<ModelPortfolioCandidatesResponse>(
    `/public/flow/portfolios/${portfolioId}/model-candidates${query}`,
    token,
  );
}

export function deletePublicPortfolio(portfolioId: string, token: string) {
  return delWithToken<{ success: boolean }>(`/public/flow/portfolios/${portfolioId}`, token);
}

// --- Portfolio Products ---

export function addPublicPortfolioProduct(
  portfolioId: string,
  data: Record<string, unknown>,
  token: string,
) {
  return postWithToken<any>(`/public/flow/portfolios/${portfolioId}/products`, token, data);
}

export function updatePublicPortfolioProduct(
  productId: string,
  data: Record<string, unknown>,
  token: string,
) {
  return patchWithToken<any>(`/public/flow/portfolios/products/${productId}`, token, data);
}

export function deletePublicPortfolioProduct(productId: string, token: string) {
  return delWithToken<any>(`/public/flow/portfolios/products/${productId}`, token);
}

// --- Existing Portfolio Snapshot ---

export function getPublicExistingSnapshot(portfolioId: string, token: string) {
  return getWithToken<ExistingSnapshotResponse>(
    `/public/flow/portfolios/${portfolioId}/existing-snapshot`,
    token,
  );
}

/** Keep/sell decision on an unmatchable holding (QL-283 decision 18 —
 * unified action parity: same rules and service as the advisor surface). */
export function updatePublicExistingHolding(
  portfolioId: string,
  positionId: string,
  data: UpdateExistingHoldingRequest & { lang?: string },
  token: string,
) {
  return patchWithToken<UpdateExistingHoldingResponse>(
    `/public/flow/portfolios/${portfolioId}/existing-snapshot/${positionId}`,
    token,
    data,
  );
}

// --- Switch pairs (QL-283) ---

export function addPublicSwitchPair(
  portfolioId: string,
  data: AddSwitchPairRequest,
  token: string,
) {
  return postWithToken<SwitchPairResponse>(
    `/public/flow/portfolios/${portfolioId}/switch-pairs`,
    token,
    data satisfies AddSwitchPairRequest,
  );
}

export function updatePublicSwitchPair(
  portfolioId: string,
  pairId: string,
  data: UpdateSwitchPairRequest,
  token: string,
) {
  return patchWithToken<SwitchPairResponse>(
    `/public/flow/portfolios/${portfolioId}/switch-pairs/${pairId}`,
    token,
    data satisfies UpdateSwitchPairRequest,
  );
}

export function deletePublicSwitchPair(portfolioId: string, pairId: string, token: string) {
  return delWithToken<SwitchPairDeleteResponse>(
    `/public/flow/portfolios/${portfolioId}/switch-pairs/${pairId}`,
    token,
  );
}

/** "Clear switch" — every pair of one source holding, one server-side
 * snapshot regeneration. */
export function clearPublicSwitchPairs(
  portfolioId: string,
  sourcePositionId: string,
  token: string,
) {
  return delWithToken<SwitchPairClearResponse>(
    `/public/flow/portfolios/${portfolioId}/switch-pairs?sourcePositionId=${encodeURIComponent(sourcePositionId)}`,
    token,
  );
}
